import { useCallback, useEffect, useState } from "react";

import { VideoState } from "./types";

export type SyncAction =
  | { type: "is-playing" }
  | { type: "is-paused" }
  | { type: "changed-duration"; value: number }
  | { type: "changed-progress"; value: number }
  | { type: "changed-volume"; value: number }
  | { type: "changed-fullscreen"; value: boolean }
  | {
      type: "initialize";
      value: { soundLevel: number; currentTime: number };
    };

export function useVideoCommandHandler() {
  const [exposedState, setExposedState] = useState<VideoState>({
    state: "paused",
    soundLevel: undefined,
    currentTime: undefined,
    fullScreen: false,
    hasInteracted: false,
  });

  const dispatchSync = useCallback((action: SyncAction) => {
    // console.log(`[SYNC] ${action.type}`, action);

    switch (action.type) {
      case "initialize":
        return setExposedState(s => ({ ...s, ...action.value, hasInteracted: true }));
      case "is-paused":
        return setExposedState(s => ({ ...s, state: "paused", hasInteracted: true }));
      case "is-playing":
        return setExposedState(s => ({ ...s, state: "playing", hasInteracted: true }));
      case "changed-duration":
        return setExposedState(s => ({ ...s, duration: action.value, hasInteracted: true }));
      case "changed-progress":
        return setExposedState(s => ({ ...s, currentTime: action.value, hasInteracted: true }));
      case "changed-volume":
        return setExposedState(s => ({ ...s, soundLevel: action.value, hasInteracted: true }));
      case "changed-fullscreen":
        return setExposedState(s => ({ ...s, fullScreen: action.value, hasInteracted: true }));

      default: {
        const _: never = action;

        return _;
      }
    }
  }, []);

  useEffect(() => {
    const f = () => {
      if (
        !document.fullscreenElement &&
        !(document as any).webkitFullscreenElement &&
        !(document as any).mozFullScreenElement
      ) {
        // console.log("fs - off");
        setExposedState(s => ({ ...s, fullScreen: false }));
      } else {
        // console.log("fs - on");
        setExposedState(s => ({ ...s, fullScreen: true }));
      }
    };

    document.addEventListener("webkitfullscreenchange", f);
    document.addEventListener("mozfullscreenchange", f);
    document.addEventListener("fullscreenchange", f);

    return () => {
      document.removeEventListener("webkitfullscreenchange", f);
      document.removeEventListener("mozfullscreenchange", f);
      document.removeEventListener("fullscreenchange", f);
    };
  }, [exposedState.fullScreen]);

  return {
    exposedState,
    dispatchSync,
  };
}
